/**
 * useNovaSuggestions — runs the rules engine over live CRM data.
 *
 * Pulls from the existing react-query hooks (so it benefits from
 * cache + realtime invalidation already wired in /services/*) and
 * reruns the engine whenever any input changes.
 *
 * Returns a ranked list. Pages typically render the top one with
 * `<NovaCard suggestion={s} />` and may show the next 2 in a stack.
 */

import { useEffect, useMemo, useState } from 'react';
import { useClients } from '@/services/clients';
import { useProjects } from '@/services/projects';
import { useInquiries } from '@/services/inquiries';
import { useGoals, ringDone, ringProgress, type Goal } from '@/services/passport';
import { useNovaEmotion } from '@/components/nova/NovaEmotion';
import { useNovaIntelligence } from '@/hooks/useNovaIntelligence';
import { computeNovaSuggestions, type RuleInput } from '@/lib/novaRules';
import type { NovaSuggestion } from '@/types/novaSuggestion';
// Wave 8.2 — Preview Studio short-circuit. When isDemo, we bypass
// the rules engine (which would run against the empty demo user's
// real CRM data and turn up nothing) and hand back the pre-seeded
// suggestion cards so the keynote never sees a "no suggestions" gap.
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';

const STORAGE_KEY = 'orbit.nova.dismissed';

/** Module-level so an off switch hands back the same array every render. */
const NONE: NovaSuggestion[] = [];

/** Read the persisted set of dismissed suggestion ids from localStorage. */
function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function persistDismissed(s: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...s]));
  } catch {
    /* localStorage may be unavailable (SSR / private mode) — silent OK */
  }
}

/** One goal → the flat row novaRules reads. */
function goalRow(g: Goal): NonNullable<RuleInput['goals']>[number] {
  const counter = g.rings.filter((r) => r.kind === 'counter' && r.metric && r.target).sort((a, b) => (b.target ?? 0) - (a.target ?? 0))[0];
  const kind = counter?.metric === 'revenue' ? 'revenue' : counter?.metric === 'clients' ? 'clients' : counter?.metric === 'bookings' ? 'projects' : 'custom';
  const target_value = counter ? counter.target : g.rings.length;
  const progress_value = counter ? (ringDone(counter) ? counter.target ?? 0 : counter.value ?? 0) : g.rings.filter((r) => ringDone(r) || ringProgress(r) >= 1).length;
  return {
    id: g.id, title: g.title, kind, target_value, target_date: g.due_on, progress_value,
    status: g.archived_at ? 'archived' : g.completed_at ? 'achieved' : 'active',
  };
}

export interface UseNovaSuggestionsResult {
  /** Top suggestion (highest priority, most recent). undefined if none. */
  top: NovaSuggestion | undefined;
  /** All suggestions, ranked. */
  all: NovaSuggestion[];
  /** Mark a suggestion as dismissed — won't surface again until cleared. */
  dismiss: (id: string) => void;
  /** Clear all dismissals (for the "show me everything" toggle). */
  clearDismissed: () => void;
  /** Whether any of the underlying data is still loading. */
  isLoading: boolean;
}

export function useNovaSuggestions(): UseNovaSuggestionsResult {
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  const [novaOn] = useNovaIntelligence();

  const { data: clients = [], isLoading: cLoading } = useClients();
  const { data: projects = [], isLoading: pLoading } = useProjects();
  const { data: inquiries = [], isLoading: iLoading } = useInquiries();
  const { data: passportGoals = [], isLoading: gLoading } = useGoals();
  // PASSPORT-2 — the rules engine reads one number per goal: the
  // biggest counter ring (its live value against its target), or the
  // steps closed over the steps set when a goal has no counter.
  const goals = useMemo(() => passportGoals.map(goalRow), [passportGoals]);

  // Deliverables aren't loaded globally — load only the projects'
  // ids and let rules see "no deliverable data" gracefully. For more
  // signal we'd add a useAllDeliverables hook later.
  const deliverables: RuleInput['deliverables'] = useMemo(() => [], []);

  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());

  // Always reflect dismissals to storage so they survive reload.
  useEffect(() => {
    persistDismissed(dismissed);
  }, [dismissed]);

  // Wave 8.2 — Preview Studio short-circuit. Reading the store's
  // `list()` on every render is cheap (it's just a ref accessor)
  // and re-derives after any dismissal via `demoStore.version`.
  const demoAll = useMemo(() => {
    if (!isDemo) return null;
    return demoStore.novaSuggestions.list() as unknown as NovaSuggestion[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, demoStore.version]);

  const all = useMemo(() => {
    if (demoAll) return demoAll;
    const ranked = computeNovaSuggestions({
      clients: clients as RuleInput['clients'],
      projects: projects as unknown as RuleInput['projects'],
      inquiries: inquiries as unknown as RuleInput['inquiries'],
      deliverables,
      goals,
    });
    return ranked.filter((s) => !dismissed.has(s.id));
  }, [demoAll, clients, projects, inquiries, deliverables, goals, dismissed]);

  const dismiss = (id: string) => {
    if (isDemo) {
      demoStore.novaSuggestions.dismiss(id);
      return;
    }
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const clearDismissed = () => {
    if (isDemo) {
      demoStore.novaSuggestions.clearDismissed();
      return;
    }
    setDismissed(new Set());
  };

  // NOVA-OFF-2 — Settings → Nova lists "Suggestions" among what turns
  // off. The rules engine is local, so nothing reached a model either
  // way, but the cards are hers and the switch has to empty them.
  const visible = novaOn ? all : NONE;

  return {
    top: visible[0],
    all: visible,
    dismiss,
    clearDismissed,
    // In Preview Studio the demo seed is already resolved so we
    // don't gate the surface on the real queries settling.
    isLoading: isDemo ? false : cLoading || pLoading || iLoading || gLoading,
  };
}

/**
 * Variant that also nudges Nova's ambient mood based on the top
 * suggestion's `novaState` field. Drop this on any page that owns a
 * floating Nova so she visually reflects what she's noticed.
 */
export function useNovaSuggestionsWithAmbient(): UseNovaSuggestionsResult {
  const result = useNovaSuggestions();
  const { setBaseline } = useNovaEmotion();

  useEffect(() => {
    if (result.top?.novaState) {
      setBaseline(result.top.novaState);
    }
  }, [result.top?.novaState, setBaseline]);

  return result;
}
